import i18n from 'i18next';
import type { CustomDetector } from 'i18next-browser-languagedetector';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { SupportedLanguages, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './constants';
import DE from './DE.json';
import EN from './EN.json';

export const defaultNS = 'translation';
export const resources = {
  [SupportedLanguages.EN]: EN,
  [SupportedLanguages.DE]: DE,
};

const normalizeNavigatorLanguage = (language?: string) => {
  try {
    if (!language) {
      return DEFAULT_LANGUAGE;
    }
    const normalizedLanguage = language.split('-')[0].toUpperCase();
    if (SUPPORTED_LANGUAGES.includes(normalizedLanguage as SupportedLanguages)) {
      return normalizedLanguage;
    }
    return DEFAULT_LANGUAGE;
  } catch (e) {
    return DEFAULT_LANGUAGE;
  }
};

export const initI18Next = async (geoLanguage?: SupportedLanguages) => {
  const geoLanguageDetector = (language?: SupportedLanguages): CustomDetector => ({
    name: 'geoLanguageDetector',
    lookup(_options) {
      return language;
    },
  });

  const browserLanguage = normalizeNavigatorLanguage(navigator.language);

  // if navigator language is not the default language (english), we want to prioritize the navigator language
  const order =
    browserLanguage !== DEFAULT_LANGUAGE ? ['navigator', 'geoLanguageDetector'] : ['geoLanguageDetector', 'navigator'];

  // detect language from browser
  const languageDetector = new LanguageDetector(null, {
    convertDetectedLanguage: (lng) => lng.toUpperCase(),
    order: [
      'querystring',
      'hash',
      'cookie',
      'localStorage',
      'sessionStorage',
      ...order,
      'htmlTag',
      'path',
      'subdomain',
    ],
  });

  languageDetector.addDetector(geoLanguageDetector(geoLanguage));

  // eslint-disable-next-line import/no-named-as-default-member
  await i18n
    .use(languageDetector)
    .use(initReactI18next)
    .init({
      resources,
      supportedLngs: SUPPORTED_LANGUAGES,
      fallbackLng: DEFAULT_LANGUAGE,
      debug: false,
      interpolation: {
        escapeValue: false, // React already does escaping
        format: (value, format) => {
          switch (format) {
            case 'sweepsOrSuper':
              return value ? 'Sweeps' : 'Super';
            case 'sweepstakesOrSuper':
              return value ? 'Sweepstakes' : 'Super';
            default:
              return value;
          }
        },
      },
      defaultNS,
      ns: [defaultNS],
      keySeparator: '.',
      nsSeparator: ':',
      returnEmptyString: false,
      react: {
        useSuspense: false,
        transSupportBasicHtmlNodes: true,
        transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'b'],
      },
    });
};

export default i18n;
